/*
Muestra un conjunto de imagenes.
Autor: Benjamín León
Fecha: 2024-08-07
Modificado: 2025-09-30 - Added HEIC detection for legacy images
*/
import React, { useState } from 'react'

import { makeSmallImage } from '../utils/makeImage'
import useImageFetcher from '../hooks/useImageFetcher'

import ConfirmationModal from './ConfirmationModal'
import ImageLightbox from './ImageLightbox'
import HeicSafeImage from './HeicSafeImage'

interface ImageGalleryProps {
  onPictureDeleted: (pictureId: string) => void
  canModify: boolean
  imageUrls: string[]
  setImageUrls: (imgs: string[]) => void
  // Selection mode support
  selectionMode?: boolean
  selectedImages?: string[]
  onImageSelectionChange?: (imageUrl: string) => void
}

const ImageGallery: React.FC<ImageGalleryProps> = ({
  onPictureDeleted,
  canModify,
  imageUrls,
  setImageUrls,
  selectionMode = false,
  selectedImages = [],
  onImageSelectionChange,
}) => {
  // Para el drag and drop
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false)
  const [selectedItemId, setSelectedItemId] = useState<string>('')
  const [isLightboxOpen, setIsLightboxOpen] = useState(false)
  const [lightboxInitialIndex, setLightboxInitialIndex] = useState(0)
  const { fetchImage, errorImage } = useImageFetcher()
  // Update local state whenever initialValue changes

  // Definición de un helper para sanitizar las urls sobre la marcha
  const getImageUrlString = (url: unknown): string => {
    if (typeof url === 'string') return url

    if (url && typeof url === 'object') {
      const urlObj = url as Record<string, unknown>

      return urlObj._id?.toString() || urlObj.id?.toString() || url.toString()
    }

    return String(url)
  }

  // Function to open the image in lightbox
  const openImageInLightbox = (index: number) => {
    setLightboxInitialIndex(index)
    setIsLightboxOpen(true)
  }

  const closeLightbox = () => {
    setIsLightboxOpen(false)
  }

  const downloadImage = async (url: string) => {
    fetchImage(url)
  }

  const [draggedIndex, setDraggedIndex] = useState<number | null>(null)

  const handleDragStart = (index: number) => {
    setDraggedIndex(index)
  }

  const handleDragEnter = (index: number) => {
    if (draggedIndex === null) return

    if (draggedIndex === index) return // Evita re-renders innecesarios cuando el cursor se mueve dentro de la misma imagen

    const newImageUrls = [...imageUrls]
    const draggedItem = newImageUrls[draggedIndex]

    // Remove the dragged item and insert it at the new index
    newImageUrls.splice(draggedIndex, 1)
    newImageUrls.splice(index, 0, draggedItem)

    setImageUrls(newImageUrls)
    setDraggedIndex(index) // Update the dragged index to the new position
  }

  const handleDragEnd = () => {
    setDraggedIndex(null) // Reset the dragged index
  }
  // End drag & drop

  // Para el delete
  const deleteClick = async (id: string) => {
    setSelectedItemId(id) // Store the item ID in the state
    setIsModalOpen(true)
  }
  const handleConfirm = async () => {
    setIsModalOpen(false)
    // Perform delete action here
    onPictureDeleted(selectedItemId)
    console.log('Item deleted:' + selectedItemId)
    setSelectedItemId('')
  }

  const handleCancel = () => {
    setIsModalOpen(false)
    setSelectedItemId('')
  }

  // End delete
  return (
    <div
      className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 p-4"
      onDragOver={(e) => e.preventDefault()} // Previene animación de rebote del navegador
    >
      {imageUrls.map((url, index) => (
        <div
          key={index} // Restaurado a index: mover el nodo DOM interrumpe el evento "drag" nativo en Chrome
          className={`group relative w-full h-64 bg-gray-200 rounded overflow-hidden transition-all duration-200 ${
            selectionMode && selectedImages.includes(getImageUrlString(url))
              ? 'ring-2 ring-blue-500'
              : 'hover:shadow-lg hover:scale-[1.02]'
          }`}
          // Conditionally add "draggable" only if "canModify" is true and not in selection mode
          {...(canModify && !selectionMode && { draggable: true })}
          onDragStart={canModify && !selectionMode ? () => handleDragStart(index) : undefined}
          onDragEnter={canModify && !selectionMode ? () => handleDragEnter(index) : undefined}
          onDragEnd={canModify && !selectionMode ? handleDragEnd : undefined}
          onClick={() => openImageInLightbox(index)}
        >
          <HeicSafeImage
            src={makeSmallImage(getImageUrlString(url))}
            alt={`Imagen ${index + 1}`}
            className="w-full h-full object-cover cursor-pointer transition-opacity duration-200 hover:opacity-90"
            title="Clic para ver en pantalla completa"
          />

          {/* Lightbox indicator overlay */}
          <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors duration-200 flex items-center justify-center opacity-0 group-hover:opacity-100 pointer-events-none">
            <div className="bg-white/90 rounded-full p-2 backdrop-blur-sm">
              <svg className="w-6 h-6 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v3m0 0v3m0-3h3m-3 0H7"
                />
              </svg>
            </div>
          </div>

          {/* Selection checkbox */}
          {selectionMode && (
            <div className="absolute top-2 left-2">
              <input
                type="checkbox"
                className="w-5 h-5 rounded border-gray-300 bg-white shadow-sm focus:ring-2 focus:ring-blue-500"
                checked={selectedImages.includes(getImageUrlString(url))}
                onChange={() => onImageSelectionChange?.(getImageUrlString(url))}
              />
            </div>
          )}

          {/* Action buttons - hidden in selection mode */}
          {!selectionMode && (
            <div className="absolute top-2 right-2 flex flex-col space-y-2">
              {/* Delete Button */}
              {canModify && (
                <button
                  className="p-2 bg-white rounded-full shadow hover:bg-gray-100"
                  aria-label="Delete Image"
                  onClick={(e) => {
                    e.stopPropagation()
                    deleteClick(getImageUrlString(url))
                  }}
                >
                  🗑️
                </button>
              )}
              {/* View Button */}
              <button
                className="p-2 bg-white rounded-full shadow hover:bg-gray-100"
                aria-label="View Image"
                onClick={(e) => {
                  e.stopPropagation()
                  openImageInLightbox(index)
                }}
              >
                🔍
              </button>

              {/* Download Button */}
              <button
                className="p-2 bg-white rounded-full shadow hover:bg-gray-100"
                aria-label="Download Image"
                onClick={(e) => {
                  e.stopPropagation()
                  downloadImage(getImageUrlString(url))
                }}
              >
                ⬇️
              </button>
            </div>
          )}
        </div>
      ))}

      {isModalOpen && (
        <ConfirmationModal
          message="¿Estás seguro de eliminar esta imagen?"
          onConfirm={handleConfirm}
          onCancel={handleCancel}
        />
      )}

      {errorImage?.message}

      {/* Image Lightbox */}
      <ImageLightbox
        isOpen={isLightboxOpen}
        onClose={closeLightbox}
        imageUrls={imageUrls}
        initialIndex={lightboxInitialIndex}
        onDownload={(imageUrl) => {
          // Use existing download logic
          downloadImage(imageUrl)
        }}
        onDelete={
          canModify
            ? (imageUrl) => {
                // Use existing delete logic
                onPictureDeleted(imageUrl)
              }
            : undefined
        }
        canDelete={canModify}
      />
    </div>
  )
}

export default ImageGallery
